import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import BillingData from "features/adesao/steps/BillingData";
import Meter from "features/adesao/steps/Meter";
import PersonalData from "features/adesao/steps/PersonalData";
import Registration from "features/adesao/steps/Registration";
import SuccessRegister from "features/adesao/steps/SuccessRegister";
import ValidateRegistration from "features/adesao/steps/ValidateRegistration";

import type { AdesaoHeader } from "features/adesao/types";

const STEPPERS = [
  "Identificação",
  "Dados Cadastrais",
  "Cadastre sua residência ou loja",
  "Medidor",
  "Dados de Cobrança",
  "Cadastro de senha",
  "Conclusão",
];

function mapStep(step: number): number {
  return step === 1 ? 2 : step;
}

export function useAdesao(): {
  step: number;
  header: AdesaoHeader;
  steppers: string[];
  stepView: React.ReactNode;
} {
  const history = useHistory();
  const { step, document, phoneDdd, phoneNumber } = useSelector(
    (state: {
      membershipReducer: {
        step: number;
        document: unknown;
        phoneDdd: string;
        phoneNumber: string;
      };
    }) => ({
      step: state.membershipReducer.step,
      document: state.membershipReducer.document,
      phoneDdd: state.membershipReducer.phoneDdd,
      phoneNumber: state.membershipReducer.phoneNumber,
    })
  );

  useEffect(() => {
    if (!document) {
      history.push("/");
    }
  }, [document]);

  const header = useMemo(() => {
    const s = mapStep(step);

    return (
      {
        1: { h1: "", h2: "" },
        2: {
          h1: "Dados cadastrais",
          h2: "Agora informe seus dados pessoais",
        },
        3: {
          h1: "Cadastre sua residência ou loja",
          h2: "Digite o CEP ou o código do condomínio",
        },
        4: {
          h1: "Possui medidor?",
          h2: "Verifique o exemplo a seguir",
        },
        5: {
          h1: "Dados da cobrança",
          h2: "Cadastre com apenas alguns cliques a melhor opção para o recebimento de suas faturas.",
        },
        5.1: {
          h1: "Entre em contato com a Unidade de Atendimento Cliente em caso de emancipação",
          h2: "",
        },
        6: {
          h1: "Cadastro de senha",
          h2: "Estamos quase lá! Escolha uma senha.",
        },
        7: {
          h1: "Tudo certo!!",
          h2: "Agora você já pode acessar seu portal e visualizar todas as suas unidades em um único lugar.",
        },
      }[s] || { h1: "", h2: "" }
    );
  }, [step, phoneDdd, phoneNumber]);

  const stepView = useMemo(() => {
    const s = mapStep(step);

    return (
      {
        2: <PersonalData />,
        3: <Registration />,
        4: <Meter />,
        5: <BillingData />,
        6: <ValidateRegistration />,
        7: <SuccessRegister />,
      }[s] || <Registration />
    );
  }, [step]);

  return {
    step,
    header,
    steppers: STEPPERS,
    stepView,
  };
}
